// Вокабуляры каталога `pothos_assets` — ЕДИНСТВЕННЫЙ источник (2026-08-14).
//
// Почему это отдельный модуль, а не пять inline-`z.enum` по месту:
//
//   • одни и те же четыре категории были объявлены ЗАНОВО минимум в пяти
//     файлах контрактов, в разном порядке, а в двух местах — просто
//     `z.string()` с перечислением в комментарии. Комментарий не проверяется
//     ничем и протухает молча;
//   • комментарий к DDL `source_pipeline` объявлял пять значений
//     (`work-photographer|work-generator|pothos-donor|local-import|tracker`),
//     а в проде их ОДИННАДЦАТЬ, и из документированных живы два. Крупнейший
//     (`pothos-shipment`, 62 379 строк) не был описан нигде;
//   • статус `quarantine` (827 строк) не значился ни в DDL, ни в одном
//     контракте.
//
// Правило модуля: здесь перечислено то, что РЕАЛЬНО лежит в колонке, а не то,
// что мы хотели бы там видеть. Списки сверены с прод-D1 2026-08-14. Значение,
// встреченное в базе, обязано быть здесь — иначе `parseRes` начнёт валить
// живой трафик. Сузить вокабуляр можно только миграцией данных, не правкой
// этого файла.
import { z } from 'zod';

// ─── Жизненный цикл кадра ───────────────────────────────────────────────────
// ОДНА ось статуса (см. DDL pothos_assets). `quarantine` — кадр, который
// разбирать нечего (нет уникального кода / дубль принятого оригинала):
// он не в очереди QA и не в браке, ADR-0049 + ADR-0050.
export const ASSET_STATUS = [
  'uploaded', 'in_work', 'qa', 'quarantine', 'accepted',
  'shipped', 'client_accepted', 'client_rejected', 'rejected', 'duplicate',
] as const;
export const AssetStatusSchema = z.enum(ASSET_STATUS);
export type AssetStatus = z.infer<typeof AssetStatusSchema>;

// Статусы, означающие «кадр у заказчика или уехал к нему». Кап мерчанта и
// планы локалей считаются ИМЕННО по ним — держим списком, а не строкой в SQL,
// чтобы новый статус нельзя было забыть добавить в один из десятка запросов.
export const SHIPPED_STATUSES = [
  'shipped', 'client_accepted', 'client_rejected',
] as const satisfies readonly AssetStatus[];

// Семьи ВЕРДИКТА QA. Вердикт живёт в status и после решения едет дальше по
// семье (accepted → shipped → client_*), поэтому доски и дневная статистика
// считают семьями — «принято за день» не худеет, когда принятые кадры позже
// отгружают. Держим списком здесь, а не Set'ом/строкой по месту: до 2026-08-31
// те же шесть статусов были объявлены заново в team-board.ts и daily-stats.ts.
export const QA_VERDICT_ACCEPTED_STATUSES = [
  'accepted', 'shipped', 'client_accepted',
] as const satisfies readonly AssetStatus[];
export const QA_VERDICT_REJECTED_STATUSES = [
  'rejected', 'duplicate', 'client_rejected',
] as const satisfies readonly AssetStatus[];

// ─── Категория материала ────────────────────────────────────────────────────
// КАРТОЧНЫЕ категории — конвейер pothos целиком: план локалей, упаковка,
// отгрузка, кап мерчантов, дизайнерский пул. Держим отдельным списком, чтобы
// поверхности, которым чеки не положены (генератор скриншотов, упаковка),
// сужали вход именно до него, а не до всего вокабуляра хранения.
export const CARD_ASSET_CATEGORY = [
  'event_ticket', 'coupon', 'gift_card', 'membership',
] as const;
export const CardAssetCategorySchema = z.enum(CARD_ASSET_CATEGORY);
export type CardAssetCategory = z.infer<typeof CardAssetCategorySchema>;

// `receipt` — проект «чеки» (Глеб, 2026-08-25): регистрация и QA — общим
// конвейером через pothos_assets, но после QA чек уходит в СВОЙ реестр
// `pothos_receipts` (мигр. 0082, «чеки — это прям отдельно»), минуя упаковку
// (finalizePackaging исключает категорию). В план локалей, капы мерчантов и
// дизайнерский цикл чеки не входят.
export const ASSET_CATEGORY = [...CARD_ASSET_CATEGORY, 'receipt'] as const;
export const AssetCategorySchema = z.enum(ASSET_CATEGORY);
export type AssetCategory = z.infer<typeof AssetCategorySchema>;

// ─── Модальность ────────────────────────────────────────────────────────────
// `photo_screenshot` — смешанный тип (141 строка): исторический материал, где
// не разделили съёмку и скриншот. Планово считается фотографией (planModality).
export const ASSET_MODALITY = ['photo', 'screenshot', 'photo_screenshot'] as const;
export const AssetModalitySchema = z.enum(ASSET_MODALITY);
export type AssetModality = z.infer<typeof AssetModalitySchema>;

// ─── Роль кадра ─────────────────────────────────────────────────────────────
// `source` — сырьё (скриншот мембершипа/гифта), из которого дизайнер делает
// финалы; сам по себе не отгружается. `final` — то, что уезжает заказчику.
export const ASSET_ROLE = ['final', 'source'] as const;
export const AssetRoleSchema = z.enum(ASSET_ROLE);
export type AssetRole = z.infer<typeof AssetRoleSchema>;

// ─── Источник кадра ─────────────────────────────────────────────────────────
// ВАЖНО: это поле МАРШРУТИЗИРУЕТ вердикт QA, а не только описывает кадр.
// Префикс `work` заставляет кабинет слать вердикт в work-api `reviewFrame`
// (там пересчёт баланса earner'а); всё остальное идёт в индекс через
// `setAssetVerdict`. Импорт, который пишет ТОЛЬКО в индекс, не имеет права
// называться `work-photographer` — вердикт улетит в work-api и вернётся 404
// (1 246 кадров печатной съёмки, 2026-07-30; для них заведён `print-shoot`).
export const SOURCE_PIPELINE = [
  'pothos-shipment',    // историческая отгрузка, залитая в каталог
  'work-photographer',  // съёмка в кабинете work (ДВЕ базы: work_photos + индекс)
  'print-shoot',        // импорт печатной съёмки — только индекс, без work_photos
  'work-dev',           // размножение dev-отдела (плагин Figma)
  'work-generator',     // генератор-скриншоты кабинета work
  'local-pool',         // локальный пул на машине владельца
  'designer',           // финалы дизайнеров (des_deliverables → индекс)
  'dev-replication',    // размножение исходников
  'manager-upload',     // ручная заливка менеджера
  'shipment-backfill',  // добивка исторических отгрузок
  'local-import',       // разовый импорт с диска
  'donor-template',     // донорские шаблоны
  'scrape-intake',      // скрап-материал через intake (батч + манифест, только индекс)
] as const;
export const SourcePipelineSchema = z.enum(SOURCE_PIPELINE);
export type SourcePipeline = z.infer<typeof SourcePipelineSchema>;

/** Пайплайны, чей вердикт обязан уехать в work-api, а не в индекс. */
export const WORK_ROUTED_PIPELINES = [
  'work-photographer', 'work-generator',
] as const satisfies readonly SourcePipeline[];

// ─── Источник материала для отгрузочного обзора ─────────────────────────────
// Три канала происхождения, которыми оперирует владелец (звонок 2026-08-31):
//   users       — материал, снятый/собранный людьми (фотографы, генераторы,
//                 Upwork, ручные заливки, исторические отгрузки);
//   scraping    — скрап-конвейер (intake-батчи и кадры скрейперов);
//   replication — размножение: финалы дизайнеров, dev-размножение исходников,
//                 Figma-плагин, донорские шаблоны.
// Карта тотальная по вокабуляру ХРАНЕНИЯ: новый source_pipeline без строки
// здесь роняет сборку, а не молча падает в «прочее».
export const ASSET_SOURCE_GROUP = ['users', 'scraping', 'replication'] as const;
export const AssetSourceGroupSchema = z.enum(ASSET_SOURCE_GROUP);
export type AssetSourceGroup = z.infer<typeof AssetSourceGroupSchema>;

export const SOURCE_GROUP_BY_PIPELINE = {
  'pothos-shipment': 'users',
  'work-photographer': 'users',
  'print-shoot': 'users',
  'work-dev': 'replication',
  'work-generator': 'users',
  'local-pool': 'users',
  'designer': 'replication',
  'dev-replication': 'replication',
  'manager-upload': 'users',
  'shipment-backfill': 'users',
  'local-import': 'users',
  'donor-template': 'replication',
  'scrape-intake': 'scraping',
} as const satisfies Record<SourcePipeline, AssetSourceGroup>;

// ─── Как кадр попал в систему ───────────────────────────────────────────────
// '' — историческая строка, канал не записывали.
export const UPLOADED_VIA = [
  '', 'camera', 'file', 'archive', 'designer_plugin',
  'intake',  // батч intake: байты в R2 staging мимо воркера, манифест отдельно
] as const;
export const UploadedViaSchema = z.enum(UPLOADED_VIA);
export type UploadedVia = z.infer<typeof UploadedViaSchema>;

// ─── Вердикт заказчика ──────────────────────────────────────────────────────
// ДВЕ формы «вердикта нет»: '' (38 586 строк) и 'none' (2 645). Это не
// дизайн, а след двух разных путей записи. Слить их можно только миграцией
// данных — до тех пор оба значения обязаны быть в вокабуляре, иначе parseRes
// уронит живой трафик. Проверять «вердикта нет» — через hasClientVerdict().
export const CLIENT_VERDICT = ['', 'none', 'accepted', 'rejected_by_customer'] as const;
export const ClientVerdictSchema = z.enum(CLIENT_VERDICT);
export type ClientVerdict = z.infer<typeof ClientVerdictSchema>;

/** Есть ли реальный вердикт заказчика (обе формы «нет» — false). */
export function hasClientVerdict(v: string): boolean {
  return v === 'accepted' || v === 'rejected_by_customer';
}
